'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fairtradeDarkControlClass } from '@/components/help-center/fairtrade/fairtrade-dark-panel';
import { cn } from '@/lib/utils';

type FairtradePaginationProps = {
  page: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
  total: number;
  onPageChange: (page: number) => void;
  isDark?: boolean;
};

function buildPageList(current: number, total: number): (number | 'ellipsis')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages: (number | 'ellipsis')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('ellipsis');
  for (let p = start; p <= end; p += 1) pages.push(p);
  if (end < total - 1) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

export default function FairtradePagination({
  page,
  totalPages,
  rangeStart,
  rangeEnd,
  total,
  onPageChange,
}: FairtradePaginationProps) {
  const pages = buildPageList(page, totalPages);

  return (
    <div
      className={cn(
        'relative overflow-hidden border-t border-blue-900/50',
        'bg-gradient-to-b from-[#01012e]/80 via-[#030023]/40 to-transparent',
        'px-4 py-4 sm:px-5 sm:py-5'
      )}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/35 to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-10 -top-12 h-28 w-28 rounded-full bg-sky-500/10 blur-2xl"
        aria-hidden
      />

      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="min-w-0 space-y-1">
          <p className="font-poppins text-[10px] font-semibold uppercase tracking-[0.14em] !text-sky-200/70">
            Exibindo
          </p>
          <p className="font-inter text-sm tabular-nums !text-blue-100/80">
            <span className="font-semibold !text-sky-50">
              {rangeStart}–{rangeEnd}
            </span>
            <span className="!text-blue-200/50"> de </span>
            <span className="font-semibold !text-sky-100">{total}</span>
            <span className="ml-1.5 !text-blue-200/65">powers</span>
          </p>
        </div>

        <nav
          aria-label="Paginação do Fairtrade"
          className={cn(
            'inline-flex flex-wrap items-center justify-center gap-1.5 self-start rounded-2xl border border-sky-400/20 p-1.5',
            'bg-[#01012e]/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]',
            'sm:self-auto'
          )}
        >
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-xl transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
              'disabled:pointer-events-none disabled:opacity-35',
              fairtradeDarkControlClass,
              '!text-sky-100/90 hover:!text-sky-50'
            )}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>

          <div className="flex items-center gap-0.5 rounded-xl bg-[#030023]/60 p-0.5">
            {pages.map((item, idx) =>
              item === 'ellipsis' ? (
                <span
                  key={`e-${idx}`}
                  className="inline-flex h-9 w-7 items-center justify-center font-poppins text-sm !text-blue-200/35"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => onPageChange(item)}
                  className={cn(
                    'inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-2.5',
                    'font-poppins text-sm font-semibold tabular-nums transition-all duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
                    item === page
                      ? 'bg-sky-400/20 !text-sky-50 shadow-sm ring-1 ring-sky-400/40'
                      : '!text-blue-100/70 hover:bg-white/[0.04] hover:!text-sky-100'
                  )}
                  aria-current={item === page ? 'page' : undefined}
                >
                  {item}
                </button>
              )
            )}
          </div>

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className={cn(
              'inline-flex h-9 w-9 items-center justify-center rounded-xl transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
              'disabled:pointer-events-none disabled:opacity-35',
              fairtradeDarkControlClass,
              '!text-sky-100/90 hover:!text-sky-50'
            )}
            aria-label="Próxima página"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </nav>
      </div>

      {totalPages > 1 ? (
        <p className="relative mt-3 font-poppins text-[10px] font-medium uppercase tracking-wide !text-sky-200/55">
          Página {page} de {totalPages}
        </p>
      ) : null}
    </div>
  );
}
